import type { PassTemplateMeta } from '../../../../store/types/pass';

export type FieldSlot = [string, string, { id: string } | undefined];

/** Check whether a locale entry has actual translation content (non-empty label or defaultValue). */
export const hasLocaleContent = (loc: unknown): boolean => {
  if (!loc || typeof loc !== 'object') return false;
  const entry = loc as Record<string, unknown>;
  return !!(entry.label || entry.defaultValue);
};

/** Build the field slot array from a template, mapping section/slot to field definitions. */
export const getTemplateFieldSlots = (
  template: PassTemplateMeta
): FieldSlot[] => [
  ['header', 'one', template.header?.header_one],
  ['header', 'two', template.header?.header_two],
  ['header', 'three', template.header?.header_three],
  ['central', 'one', template.primary?.primary_one],
  ['central', 'two', template.primary?.primary_two],
  ['details', 'one', template.secondary?.sec_one],
  ['details', 'two', template.secondary?.sec_two],
  ['details', 'three', template.secondary?.sec_three],
  ['details', 'four', template.auxiliary?.aux_one],
  ['details', 'five', template.auxiliary?.aux_two],
  ['details', 'six', template.auxiliary?.aux_three],
  ['back', 'one', template.back_fields?.back_one],
  ['back', 'two', template.back_fields?.back_two],
  ['back', 'three', template.back_fields?.back_three],
  ['back', 'four', template.back_fields?.back_four],
  ['back', 'five', template.back_fields?.back_five],
];

/** Mapping from slot keys to preview data label/value keys. */
const PREVIEW_KEY_MAP: Record<string, { labelKey: string; valueKey: string }> = {
  'header.one': { labelKey: 'headerOneLabel', valueKey: 'headerOneValue' },
  'header.two': { labelKey: 'headerTwoLabel', valueKey: 'headerTwoValue' },
  'header.three': { labelKey: 'headerThreeLabel', valueKey: 'headerThreeValue' },
  'central.one': { labelKey: 'primaryOneLabel', valueKey: 'primaryOneValue' },
  'central.two': { labelKey: 'primaryTwoLabel', valueKey: 'primaryTwoValue' },
  'details.one': { labelKey: 'detailsOneLabel', valueKey: 'detailsOneValue' },
  'details.two': { labelKey: 'detailsTwoLabel', valueKey: 'detailsTwoValue' },
  'details.three': { labelKey: 'detailsThreeLabel', valueKey: 'detailsThreeValue' },
  'details.four': { labelKey: 'detailsFourLabel', valueKey: 'detailsFourValue' },
  'details.five': { labelKey: 'detailsFiveLabel', valueKey: 'detailsFiveValue' },
  'details.six': { labelKey: 'detailsSixLabel', valueKey: 'detailsSixValue' },
  'back.one': { labelKey: 'backOneLabel', valueKey: 'backOneValue' },
  'back.two': { labelKey: 'backTwoLabel', valueKey: 'backTwoValue' },
  'back.three': { labelKey: 'backThreeLabel', valueKey: 'backThreeValue' },
  'back.four': { labelKey: 'backFourLabel', valueKey: 'backFourValue' },
  'back.five': { labelKey: 'backFiveLabel', valueKey: 'backFiveValue' },
};

/** Map template field IDs to preview data keys. */
export const buildFieldOverrides = (
  template: PassTemplateMeta,
  fieldValues: Record<string, string>
): Record<string, string> => {
  const overrides: Record<string, string> = {};
  for (const [section, position, fieldDef] of getTemplateFieldSlots(template)) {
    if (!fieldDef?.id || fieldValues[fieldDef.id] === undefined) continue;
    const keys = PREVIEW_KEY_MAP[`${section}.${position}`];
    if (keys) overrides[keys.valueKey] = fieldValues[fieldDef.id];
  }
  return overrides;
};

type TemplateFieldData = {
  localisation?: Record<string, { label?: string; defaultValue?: string }>;
};

/** Build label and default value overrides from template field localisation for a given locale. */
export const buildLocalisedLabelOverrides = (
  templateFields: Record<string, Record<string, TemplateFieldData | null>> | undefined,
  locale: string
): Record<string, string> => {
  if (!templateFields) return {};

  const overrides: Record<string, string> = {};
  for (const [slot, { labelKey, valueKey }] of Object.entries(PREVIEW_KEY_MAP)) {
    const [section, position] = slot.split('.');
    const field = templateFields[section]?.[position];
    if (!field?.localisation?.[locale]) continue;
    const loc = field.localisation[locale];
    if (loc.label) overrides[labelKey] = loc.label;
    if (loc.defaultValue) overrides[valueKey] = loc.defaultValue;
  }
  return overrides;
};
